import React, { useState, useEffect } from 'react';
import { Empty, Spin, notification } from 'antd';

import Doughnut from 'components/Charts/Doughnut';
import Sidebar from './Sidebar';

import Api from 'config/api';
import { formatNumberAsCurrency } from 'utils/helpers';

import './index.scss';

const defaultBudgetYear = new Date().getFullYear();

export default function BudgetOverview() {
  const [loading, setLoading] = useState(false);
  const [mdas, setMdas] = useState([]);
  const [subMdas, setSubMdas] = useState([]);
  const [selectedDate, setSelectedDate] = useState(defaultBudgetYear);
  const [selectedMda, setSelectedMda] = useState('');
  const [selectedSubMda, setSelectedSubMda] = useState('');
  const [allocations, setAllocations] = useState([]);
  const [nameOfMda, setNameOfMda] = useState('');
  const [searchingMdas, setSearchingMdas] = useState(false);
  const [selectedMdaId, setSelectedMdaId] = useState('');

  useEffect(() => {
    getMdas();
  }, []);

  useEffect(() => {
    selectedMda && search();
  }, [selectedMda, selectedDate]);

  useEffect(() => {
    getSubMdas();
  }, [selectedMdaId]);

  const getMdas = async () => {
    try {
      const response = await Api.get('MDA');
      setMdas(response?.data?.data);
      //the three lines below could be collapse into one...using an object
      setSelectedMda(response?.data?.data[0].code);
      setNameOfMda(response?.data?.data[0].name);
      setSelectedMdaId(response?.data?.data[0].id);
    } catch (error) {}
  };

  const getSubMdas = async () => {
    try {
      if (selectedMdaId) {
        const { data } = await Api.get(`MDA/${selectedMdaId}/sub-mdas`);
        setSubMdas(data?.data);
        const subMdaDescription = data?.data[0]?.description;

        if (data?.data.length > 0) {
          setSelectedSubMda(subMdaDescription);
        } else {
          setSelectedSubMda('No SubMda found');
        }
      }
    } catch ({ message, status }) {
      notification.error({
        message: `Status code: ${status}`,
        description: `Failed to load Submdas. ${message}`,
      });
    }
  };

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await Api.get('/budget/search-mda-subMda', {
        params: { code: selectedMda, type: 'mda', year: selectedDate },
      });
      setAllocations(data?.data);
    } catch (error) {}
    setLoading(false);
  };

  const handleSearchMdas = async (e) => {
    try {
      setSearchingMdas(true);
      const { data } = await Api.get(`MDA?search=${e.target.value}`, {
        search: e.target.value,
      });
      setMdas(data?.data);
    } catch (error) {}
    setSearchingMdas(false);
  };

  const handleSearchSubMdas = async (e) => {
    try {
      setSearchingMdas(true);
      const { data } = await Api.get(
        `MDA/${selectedMdaId}/sub-mdas?search=${e.target.value}`,
        {
          search: e.target.value,
        }
      );
      setSubMdas(data?.data);
    } catch (error) {}
    setSearchingMdas(false);
  };

  const series = [
    allocations?.totalRecurrent || 0,
    allocations?.totalCapital || 0,
  ];

  // extract colors to constants...
  const overview = [
    {
      color: '#1E8449',
      amount: `₦${
        formatNumberAsCurrency(allocations?.totalAllocation, 2) || 0
      }`,
      name: 'Total Allocation',
    },
    {
      color: '#77B9CE',
      amount: `₦${formatNumberAsCurrency(allocations?.totalRecurrent, 2) || 0}`,
      name: 'Recurrent Expenditure',
    },
    {
      color: '#B32E2E',
      amount: `₦${formatNumberAsCurrency(allocations?.totalCapital, 2) || 0}`,
      name: 'Capital  Expenditure',
    },
  ];
  //has been extracted out...
  const renderbudgetListItem = ({ item }) => (
    <div className='budgetListItem'>
      <div
        style={{
          border: `3px solid ${item.color}`,
          width: '20px',
          background: `${item.color}`,
        }}
      ></div>
      <p className='mb-0'>{item.amount}</p>
      <p className='mb-0'>{item.name}</p>
    </div>
  );

  const noChart =
    allocations.totalAllocation === 0 &&
    allocations.totalCapital === 0 &&
    allocations.totalRecurrent === 0;

  return (
    <Spin spinning={loading}>
      <div className='budget-overview'>
        <div className='budget-overview__side'>
          <Sidebar
            mdas={mdas}
            subMdas={subMdas}
            nameOfSelectedMda={nameOfMda}
            nameOfSelectedSubMda={selectedSubMda}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            handleSearchMdas={handleSearchMdas}
            handleSearchSubMdas={handleSearchSubMdas}
            handleSearch={search}
            searchingMdas={searchingMdas}
            setSelectedMda={(code, mdaTitle, nameOfMda, mdaId) => {
              if (mdaTitle === 'MDA') {
                //would pull everything below and use a single object...later
                setSelectedMda(code);
                setNameOfMda(nameOfMda);
                setSelectedMdaId(mdaId);
              } else {
                setSelectedSubMda(code);
                setSelectedSubMda(nameOfMda);
              }
            }}
          />
        </div>
        <div className='budget-overview__main'>
          <div className='budget-overview__heading'>
            <h1 style={{ fontWeight: '700' }}>{nameOfMda.replace('↵', ' ')}</h1>
          </div>
          <div className='budget-overview__details'>
            <div
              className='d-flex align-items-center justify-content-center'
              style={{ minWidth: '300px' }}
            >
              {noChart ? (
                <Empty />
              ) : (
                series.length > 0 && <Doughnut series={series} />
              )}
            </div>
            <div className='budget-overview__details__main'>
              {overview.map((item, index) => (
                <React.Fragment key={index}>
                  {renderbudgetListItem({ item })}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className='budget-overview__footer'>
            <a
              style={{
                color: '#1E8449',
                textDecoration: 'underline',
                fontSize: '16px',
              }}
              href='https://mbnp-web.netlify.app/citizens/budget'
              target='_blank'
              rel='noreferrer'
            >
              View All
            </a>
          </div>
        </div>
      </div>
    </Spin>
  );
}
